import { type FC } from 'react';
import { Controller } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  Stack,
  TextField,
} from '@mui/material';

import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';
import {
  FixtureTypeLabel,
  UnitOptions,
} from '#apps/client-web/features/audit-form-water-fixture/constants';
import { FileInput } from '#apps/client-web/features/file-storage/components/file-input';

import { type FixtureType } from '#packages/data-collection/models/audit-water-fixture-form';

import { type EditAuditFormWaterFixtureEntry } from './types';

export type AuditFormWaterFixtureEntryPageRendererProps = {
  changeFixtureType: (value: FixtureType) => void;
  form: EditAuditFormWaterFixtureEntry;
  isPending: boolean;
  onSubmit: () => void;
};

export const AuditFormWaterFixtureEntryPageRenderer: FC<
  AuditFormWaterFixtureEntryPageRendererProps
> = ({ form, isPending, changeFixtureType, onSubmit }) => {
  const { control } = form;
  const disableForm = isPending;

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      display="flex"
      flexDirection="column"
      flex={1}>
      <Stack spacing={3} sx={{ mt: 2 }} flex={1}>
        <Controller
          control={control}
          name="section"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                label="Section"
                type="text"
                variant="outlined"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="room"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                label="Room"
                type="text"
                variant="outlined"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="fixtureType"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <Autocomplete
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                getOptionLabel={(option) =>
                  FixtureTypeLabel[option as FixtureType]
                }
                options={Object.keys(FixtureTypeLabel)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Fixture Type"
                    variant="outlined"
                    {...getFormErrorAttributes(fieldState.error)}
                  />
                )}
                value={field.value ?? null}
                onChange={(_, value) => changeFixtureType(value as FixtureType)}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="quantity"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                label="Quantity"
                type="number"
                variant="outlined"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="usageRate"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                label="Usage Rate"
                type="number"
                variant="outlined"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="unit"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <Autocomplete
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                options={UnitOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Fixture Unit"
                    variant="outlined"
                    {...getFormErrorAttributes(fieldState.error)}
                  />
                )}
                value={field.value ?? null}
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="comments"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                label="Comments"
                type="text"
                multiline
                rows={3}
                variant="outlined"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        {/* Attachments */}
        <Controller
          control={control}
          name="attachments"
          disabled={disableForm}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <FileInput
                {...field}
                {...getFormErrorAttributes(fieldState.error)}
                label="Attachments"
              />
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
      </Stack>
    </Box>
  );
};
